import React from 'react';
import {
  Flex,
  Icon,
  FlexProps,
  ScaleFade,
  Text,
  useColorModeValue,
  Spacer,
} from '@chakra-ui/react';
import { IconType } from 'react-icons';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavItemProps extends FlexProps {
  icon: IconType | React.ElementType | undefined;
  href: string;
  children: string;
  openMenu: boolean;
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');

export function NavItem({ icon, href, children, openMenu, ...rest }: NavItemProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // All hooks must be called at the top level
  const bgTertiary = useColorModeValue('theme.light.tertiary', 'theme.dark.tertiary');
  const textTertiary = useColorModeValue('text.light.tertiary', 'text.dark.tertiary');
  const textPrimary = useColorModeValue('text.light.primary', 'text.dark.primary');

  const isActive = pathname === href || pathname.includes(`${href}/`);

  const labelText = React.Children.toArray(children)
    .map((child) => (typeof child === 'string' ? child : ''))
    .join('')
    .trim();
  const dataTestId = labelText ? `sidebar-link-${slugify(labelText)}` : undefined;

  return (
    <Flex
      transition="0.2s"
      w="full"
      cursor="pointer"
      align="center"
      _hover={{ bg: 'gray.100' }}
      onClick={() => navigate(href)}
      data-testid={dataTestId}
      {...rest}
    >
      <Flex
        w={openMenu ? '80%' : '95%'}
        bg={isActive ? bgTertiary : ''}
        borderRadius={isActive ? '0px 27px 27px 0px' : '0px'}
        p="8px 0 8px 15px"
        align="center"
        justify="center"
        my="4px"
      >
        {icon && (
          <Icon
            mr={openMenu ? '30px' : '0px'}
            w="25px"
            h="25px"
            as={icon}
            color={isActive ? textTertiary : textPrimary}
          />
        )}
        <ScaleFade in={openMenu} initialScale={0.5}>
          {openMenu && (
            <Text
              minW="95px"
              color={isActive ? textTertiary : textPrimary}
              fontFamily="heading"
              fontWeight="600"
            >
              {children}
            </Text>
          )}
        </ScaleFade>
        <Spacer />
      </Flex>
    </Flex>
  );
}
